import React, { Fragment, useEffect, useState } from "react";

import BreadCrumbs from "components/ui-component/BreadCrumbs";
import { CloseIcon } from "icons/close/CloseIcon";
import { CloseCircleIcon } from "icons/toast/CloseCircleIcon";
import { useAppDispatch, useAppSelector } from "store/hooks";
import Button from "components/form/Button";
import CustomModal from "components/ui-component/CustomModal";
import { useNavigate, useParams } from "react-router-dom";
import MessageToast from "components/ui-component/MessageToast";
import { paymentPlanData } from "store/slices/dashboardSlice";
import { PlanDetailsFilterList } from "utils/Options";
import { cancelPlan } from "store/slices/subscriptionSlice";
import { addNotification, useUserData } from "store/slices/authSlice";
import { SettingsValue } from "types/payments";
import {
  CancelSubscriptionSteps,
  initialCancelReasons,
} from "constants/cancelSubscriptionReason";
import { RootState } from "store/store";
import {
  CancelReason,
  PlanData,
  PlanProduct,
} from "types/subscriptionAndPlans";
import { ToastState } from "types/common";
import SuccessScreen from "components/subscriptions/SuccessScreen";
import FailedScreen from "components/subscriptions/FailScreen";
import PlanDisplay from "components/subscriptions/PlanDisplay";
import ReasonPopup from "components/subscriptions/ReasonPopup";
import { formatFrequency } from "utils/HelperFunctions";

interface CancelSubscripePlanProps {
  id?: string | null;
  show: boolean;
  setShow: any;
  setId: any;
  fetchDetails: any;
  setHandleDrawerSize?: any;
  setIsPreview?: any;
  setIsPreviewId?: any;
}

const CancelSubscribePlan = (props: CancelSubscripePlanProps) => {
  const {
    id,
    show,
    setShow,
    setId,
    fetchDetails,
    setHandleDrawerSize,
    setIsPreview,
    setIsPreviewId,
  } = props;
  const navigate = useNavigate();
  const userData = useUserData();
  const { clusterId: cluster_id } = useParams();
  const dispatch = useAppDispatch();

  const getReasonsBySettings = (): CancelReason[] => {
    if (
      financialSettings &&
      Object.keys(financialSettings).length > 0 &&
      financialSettings?.cancelation_reasons &&
      financialSettings?.cancelation_reasons.trim() !== ""
    ) {
      const validReasonArr = financialSettings.cancelation_reasons.split(",");
      return initialCancelReasons.map((item) =>
        validReasonArr.includes(item.value)
          ? { ...item, display: true, checked: false }
          : { ...item, display: false, checked: false }
      );
    }

    return initialCancelReasons.map((item) => ({ ...item, checked: false }));
  };

  // State
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [msg, setMsg] = useState<ToastState>({
    variant: "",
    content: null,
  });
  const [currentPlan, setCurrentPlan] = useState<
    PlanData | Record<string, any> | undefined
  >();
  const [productList, setProductList] = useState<PlanProduct[]>([]);
  const { companyDetails } = useAppSelector(
    (state: RootState) => state.payment
  );
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [confirmCancel, setConfirmCancel] = useState<boolean>(false);
  const [screen, setScreen] = useState<boolean>(false);
  const [reasons, setReasons] = useState<CancelReason[]>(
    initialCancelReasons.map((item) => ({ ...item, checked: false }))
  );
  const [frequency, setFrequency] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { financialSettings } = useAppSelector(
    (state: RootState) => state.auth
  );
  const [isBTNLoading, setIsBTNLoading] = useState<boolean>(false);

  // Handlers
  const handleOnClick = (id: number) => setCurrentStep(id);

  const handleCheckReason = (
    e: React.ChangeEvent<HTMLInputElement> | boolean,
    data: CancelReason,
    isTitle: boolean
  ) => {
    setReasons(
      reasons.map((item) =>
        item.id === data.id
          ? {
              ...item,
              checked: isTitle
                ? (e as boolean)
                : (e as React.ChangeEvent<HTMLInputElement>).target.checked,
            }
          : { ...item, checked: false }
      )
    );
  };

  const onClose = () => {
    setId("");
    setShow(false);
    setCurrentStep(1);
    setReasons(getReasonsBySettings());
    setCurrentPlan({});
    setOpenModal(false);
    setMessage("");
    setConfirmCancel(false);
    setScreen(false);
    setIsLoading(true);
    setProductList([]);
    if (confirmCancel) fetchDetails();
  };

  const getDisableStatus = (): boolean => {
    if (!reasons?.some((item) => item.checked)) return true;
    const isReasonChecked = reasons[7]?.checked === true;
    const isMessageEmpty = !message?.trim();
    return isReasonChecked ? isMessageEmpty : false;
  };

  // Data fetching
  const getCurrentPlan = async (): Promise<void> => {
    try {
      const payload = { id };
      const res: any = await dispatch(paymentPlanData(payload)).unwrap();
      if (res?.body?.success) {
        const data: PlanData = res?.body?.data?.clientCustomerData;
        setCurrentPlan(data);

        const filteredProducts =
          data?.productSubscriptions?.filter(
            (x: any) => x?.priceDetails?.frequency === "recurring"
          ) || [];

        setProductList(filteredProducts || []);
        const product = filteredProducts?.[0] || {};
        const frequency = formatFrequency(
          product?.priceDetails?.schedule,
          product?.priceDetails?.interval_custom_count,
          product?.priceDetails?.interval_custom_selector
        );
        setFrequency(frequency || "");
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log("getCurrentPlan error =====", error);
    }
  };

  // Cancel plan logic
  const handleCancelPlan = async ({
    btn,
  }: {
    btn: "noThanks" | "submit";
  }): Promise<void> => {
    setIsBTNLoading(true);
    try {
      let payload: {
        subscription_id: string | null | undefined;
        option: string;
        reason: string;
      };

      if (btn === "noThanks") {
        payload = {
          subscription_id: id,
          option: "",
          reason: "",
        };
      } else {
        const selectedReason = reasons.find((item) => item.checked);
        if (!selectedReason) {
          setIsBTNLoading(false);
          return;
        }
        payload = {
          subscription_id: id,
          option: selectedReason.title,
          reason:
            selectedReason.value === "other_reason"
              ? message.trim()
              : selectedReason.title,
        };
      }

      const res: any = await dispatch(cancelPlan(payload)).unwrap();
      if (res?.body?.success) {
        setConfirmCancel(true);
        setScreen(true);
        setCurrentStep((step) => step + 1);
        await dispatch(
          addNotification({
            initiated_by: "contact",
            event_type: "subscription",
            userid: userData?.data?.accountData?.leadData?.user_id,
            email: "",
            event_status: "cancelled",
            activity_type: "subscription_event",
            clusterid: (currentPlan as PlanData)?.clusterid,
            communication_status: "",
            accounts_id: userData?.data?.accountData?.id,
            payment_amount: (currentPlan as PlanData)?.amount,
            subscription_id: (currentPlan as PlanData)?.id,
            comments: `Subscription for ${userData?.data?.accountData?.accountid} was canceled`,
          })
        );
      } else {
        setCurrentStep((step) => step + 1);
        setConfirmCancel(true);
        setScreen(false);
      }
    } catch (error) {
      setCurrentStep((step) => step + 1);
      setConfirmCancel(true);
      setScreen(false);
      console.log("error =====", error);
    } finally {
      if (btn !== "noThanks") {
        setMsg({
          ...msg,
          variant: "success",
          content: `Thank you for your feedback`,
        });
      }
      setOpenModal(false);
      setIsBTNLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    if (show && id) getCurrentPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, id]);

  useEffect(() => {
    setReasons(getReasonsBySettings());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialSettings]);

  return (
    <div
      className={`relative w-full bg-white h-full overflow-y-auto lg:px-10 lg:pt-10 pt-5 pb-[10px]`}
    >
      <div className="flex items-center justify-between w-full lg:pb-[30px] pb-3 lg:px-0 px-5">
        <div className="flex items-center gap-2">
          <CloseCircleIcon strokeColor="var(--color-icon-bright)" />
          <div className="lg:text-[26px] text-lg text-txtPrimary font-medium lg:leading-[30px] leading-[23.44px]">
            Cancel your plan
          </div>
        </div>

        <div className="cursor-pointer p-2 rounded-full hover:!bg-bgSecondary">
          <CloseIcon
            onClick={(e: any) => {
              e.stopPropagation();
              e.preventDefault();
              onClose();
            }}
          />
        </div>
      </div>
      <div className="w-full lg:py-5 py-2 lg:p-0 px-5 ">
        <BreadCrumbs
          steps={CancelSubscriptionSteps}
          currentStep={currentStep}
          handleOnClick={handleOnClick}
        />
      </div>
      {confirmCancel ? (
        screen ? (
          <SuccessScreen
            onViewPlan={() => {
              setHandleDrawerSize && setHandleDrawerSize(true);
              setIsPreview && setIsPreview(true);
              setIsPreviewId && setIsPreviewId(id);
            }}
            onBackToDashboard={() => {
              navigate(`/${cluster_id}/dashboard`);
              onClose();
            }}
            id={id}
          />
        ) : (
          <FailedScreen
            onBackToDashboard={() => {
              navigate(`/${cluster_id}/dashboard`);
              onClose();
            }}
          />
        )
      ) : (
        <>
          <PlanDisplay
            isLoading={isLoading}
            productList={productList}
            currentPlan={currentPlan}
            frequency={frequency}
            financialSettings={financialSettings}
            companyDetails={companyDetails}
            userData={userData}
          />
          <div className="pt-5 lg:px-0 px-5 w-full flex flex-col gap-5">
            <Button
              text="Cancel Plan"
              onClick={() => {
                if (
                  financialSettings?.submit_cancelation_reason ===
                  SettingsValue.NO
                ) {
                  handleCancelPlan({ btn: "noThanks" });
                } else {
                  setOpenModal(true);
                }
              }}
              isDisabled={isLoading}
              isLoading={isBTNLoading}
            />
            <Button text="Back" variant="white" onClick={() => onClose()} />
          </div>
        </>
      )}
      <CustomModal
        signModal={true}
        isOpen={openModal}
        title="Subscription has been canceled"
        className="max-w-[600px] h-fit max-h-[calc(var(--shell-vh)*0.95)] rounded-[10px] overflow-y-auto"
        cancleText="Not Now"
        isSubscribeModal={true}
        onDoneClick={() => {
          // handleCancelPaymentPlan();
        }}
        onCloseClick={() => {
          setOpenModal(false);
        }}
      >
        <ReasonPopup
          reasons={reasons}
          message={message}
          isBTNLoading={isBTNLoading}
          getDisableStatus={getDisableStatus}
          handleCheckReason={handleCheckReason}
          setMessage={setMessage}
          handleCancelPlan={handleCancelPlan}
        />
      </CustomModal>
      {/* <MessageToast
        variant={msg?.variant}
        content={msg?.content}
        width="max-w-[279px] w-full px-5"
        className=" !absolute !right-1/2 !translate-x-1/2"
        isSubscribe={true}
        onCloseClick={() =>
          setMsg({
            ...msg,
            content: null,
          })
        }
      /> */}
    </div>
  );
};

export default CancelSubscribePlan;
